import React, { useEffect, useState, useRef } from "react";
import { Formio } from "@formio/react";
import { useNavigate, useParams } from "react-router-dom";
import EntityConstants from "../../configs/EntityConstants";
import Toast from "../Toast/Toast";
import { POST, GET } from "../../service/CRUDService";
import FullScreenLoader from "../FullScreenLoader/FullScreenLoader";
import CfLoader from "../CfLoader/CfLoader";
import { useTranslation } from "react-i18next";

const FormioSingleUpload = ({
  URL,
  formFieldsChanged,
  transaction_name,
  isToggleOn,
  creditCount,
  isPostPaid,
  testId = "formio-single-upload",
}) => {
  const formioMountId = `${testId}-formio`;
  const { issuer_credential_template_id } = useParams();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isSubmittingRef = useRef(false);
  const hasSufficientCredits = isPostPaid || creditCount > 0;
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [notificationMode, setNotificationMode] = useState(
    "without_notification",
  );
  const notificationModeRef = useRef(notificationMode);

  useEffect(() => {
    notificationModeRef.current = notificationMode;
  }, [notificationMode]);

  useEffect(() => {
    GET(EntityConstants.GET_ORG_DETAILS)
      .then((response) => response.json())
      .then((data) => {
        const isEnabled = data.enable_email_notification === true;

        if (isEnabled) {
          setNotificationMode("with_notification");
        } else {
          setNotificationMode("without_notification");
        }
      })
      .catch((error) => {
        console.error("Error fetching organisation details:", error);
        setNotificationMode("without_notification");
      });
  }, []);

  const onSubmitHandle = async (transaction_name, sub) => {
    if (isSubmittingRef.current) return;

    const payload = sub;
    isSubmittingRef.current = true;
    setIsSubmitting(true);

    let didNavigate = false;
    try {
      // USE STATE VALUE INSTEAD OF LOCALSTORAGE
      let apiUrl = `${EntityConstants.POST_CREDENTIALS_SINGLE_ISSUANCE}${issuer_credential_template_id}`;
      apiUrl += "&mode_of_issuance=issue";

      const response = await POST(apiUrl, payload);
      const data = await response.json();

      if (response.ok) {
        didNavigate = true;
        const params = new URLSearchParams({
          templateType: data.template_type,
          certificateName: transaction_name,
        });

        navigate(
          `/issuer/workspace/batches/summary/${data.credential_template_id}/${data.issuer_credential_template_id}?${params.toString()}`,
          {
            state: {
              transaction_id: data.transaction_id,
              programme: data.programme_name,
              batch: data.batch_name,
            },
          },
        );
      } else {
        Toast({
          type: "error",
          title: t("designs:error"),
          message: data.message,
        });
      }
    } catch (error) {
      console.error("An error occurred:", error);
    } finally {
      if (!didNavigate) {
        isSubmittingRef.current = false;
        setIsSubmitting(false);
      }
    }
  };

  const disableSubmitButton = (form) => {
    const submitButton = form.element.querySelector('button[type="submit"]');
    if (submitButton) {
      submitButton.disabled = !hasSufficientCredits;
    }
  };

  useEffect(() => {
    URL &&
      fetch(URL)
        .then((resp) => resp.json())
        .then((data) => {
          if (!transaction_name) return;

          Formio.createForm(document.getElementById(formioMountId), data).then(
            (form) => {
              form.submission = {
                data: isToggleOn ? { ...URL.data } : {},
              };
              form.on("change", formFieldsChanged);
              form.on("submit", (sub) => {
                if (isSubmittingRef.current) return;

                if (hasSufficientCredits) {
                  onSubmitHandle(
                    transaction_name,
                    sub,
                    notificationModeRef.current,
                  );
                } else {
                  Toast({
                    type: "warning",
                    title: t("designs:insufficient_credits"),
                    message: t("designs:not_enough_credits"),
                  });
                  disableSubmitButton(form);
                }
              });
              disableSubmitButton(form);
            },
          );
        });
  }, [URL, transaction_name, isToggleOn, hasSufficientCredits, formioMountId]);

  return (
    <div id={testId} className="w-full pb-2">
      <div id={formioMountId} className="flex flex-col w-full ">
        <div className="flex justify-center items-center">
          <CfLoader />
        </div>
      </div>

      {isSubmitting && <FullScreenLoader />}
    </div>
  );
};

export default FormioSingleUpload;
